import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import PopupDeletion from '../../../../../components_v2/popup/PopupDeletion';
import storeLang from '../../../../../helpers/storeLang';
import { Loader } from '../../../../../styles/global/css/GlobalLoader';
import { LoadingStateEnum } from '../../../../import/model';
import { deleteCompanyContact, getCompanyContacts } from '../../../data/action';
import { CompanyContact } from '../../../model/Model';
import { NoDataText } from '../../../style/Style';
import LinkedElementCard, { CardAvatar, CardDeleteButton } from '../LinkedElementCard';
import { CardList, SeeMore } from '../style/LinkedElementStyle';
import ContactPopup from '../../../../contact/popup/ContactPopup';
import { ModalState } from '../../../../products/model';
import { PopupContext } from '../../../../../components_v2/popup/Popup';
import { BorderColor2 } from '../../../../../styles/global/css/Utils';

interface DeletionState {
	isOpen: boolean
	contact?: CompanyContact
}


function Contact(props: {
  clientCompanyId: number
  isExtend?: boolean
  newCreation?: CompanyContact
  onDelete: () => void
}): JSX.Element {
	const DEFAULT_LIMIT = 3;

	const translate = getTranslate(storeLang.getState().localize);
	const { isChild } = React.useContext(PopupContext);
	
	const [loading, setLoading] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const [loadingMore, setLoadingMore] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const [contacts, setContacts] = React.useState<CompanyContact[]>([]);
	const [total, setTotal] = React.useState<number>(0);
	const [offset, setOffset] = React.useState<number>(0);

	const [deletionState, setDeletionState] = React.useState<DeletionState>({ isOpen: false });
	const [contactDetail, setContactDetails] = React.useState<ModalState<number>>({ isOpen: false });
	
	React.useEffect(() => {
		setLoading(LoadingStateEnum.LOADING);
		getCompanyContacts({
			client_company_id: props.clientCompanyId,
			limit: DEFAULT_LIMIT,
			offset
		}).then(response => {
			setContacts(response.contacts);
			setTotal(response.total);
			setLoading(LoadingStateEnum.LOADED);
		});
	}, []);

	React.useEffect(() => {
		if (props.newCreation != null) {
			setContacts([props.newCreation, ...contacts]);
			setOffset(offset + 1);
		}
	}, [props.newCreation]);

	function addMore(): void {
		const newOffset = offset + DEFAULT_LIMIT;
		setOffset(newOffset);
		setLoadingMore(LoadingStateEnum.LOADING);
		getCompanyContacts({
			client_company_id: props.clientCompanyId,
			limit: DEFAULT_LIMIT,
			offset: newOffset
		}).then(response => {
			setContacts(contacts.concat(response.contacts));
			setTotal(response.total > 0 ? response.total : total);
			setLoadingMore(LoadingStateEnum.LOADED);
		});
	}

	function confirmationDeletionContact(): void {
		if (!deletionState.contact) {
			return;
		}

		deleteCompanyContact({
			client_company_id: deletionState.contact.client_company_id,
			id: deletionState.contact.contact_id
		}).then(res => {
			if (res) {
				props.onDelete();
				setContacts(contacts.filter(c => c.contact_id !== deletionState.contact?.contact_id));
				setOffset(offset - 1);
				setTotal(total - 1);
			}
		});

	}

	return (
		(<CardList>
			{
				loading === LoadingStateEnum.LOADING
					? <Loader center width="30px" />
					: contacts.length > 0
						? contacts.map(c => {
							const name = `${c.contact_first_name} ${c.contact_last_name}`.trim();
							return <LinkedElementCard
								key={`cardContact${c.contact_id}`}
								isExtend={props.isExtend}
								onClick={() => isChild ? window.open(`contacts-v2?id=${c.contact_id}`) : setContactDetails({ isOpen: true, data: c.contact_id })}
								avatar={<CardAvatar photo={c.contact_photo} name={name} backgroundColor={BorderColor2} />}
								title={name}
								lines={[c.contact_position || '-', [c.contact_email, c.contact_phone].filter(Boolean).join(' · ') || '-']}
								actions={<CardDeleteButton onClick={() => setDeletionState({ ...deletionState, isOpen: true, contact: c })} />}
							/>;
						})
						: <NoDataText><Translate id='no_contact' /></NoDataText>
			}
			{
				loading === LoadingStateEnum.LOADED && loadingMore === LoadingStateEnum.LOADED && contacts.length < total &&
                <SeeMore onClick={() => addMore()}><Translate id='see_more' /></SeeMore>
			}
			{
				loading === LoadingStateEnum.LOADED && loadingMore === LoadingStateEnum.LOADING &&
                <Loader centerHorizontal width="30px" />
			}
			<PopupDeletion 
				isOpen={deletionState.isOpen} 
				records={0} 
				translationKey=''
				customTitle={'Contact'}
				customMessage={translate('global.fire.delete_message').toString().replace('{{NAME}}', `'${`${deletionState.contact?.contact_first_name ?? ''} ${deletionState.contact?.contact_last_name ?? ''}`.trim()}'`)} 
				onClickOut={() => setDeletionState({ ...deletionState, isOpen: false })}
				onValidation={() => confirmationDeletionContact()}
				noVerification
			/>
			{!isChild && <ContactPopup
				isOpen={contactDetail.isOpen}
				setOpen={isOpen => setContactDetails({ isOpen })}
				contactId={contactDetail?.data}
			/>}
		</CardList>)
	);
}

export default Contact;
